'use client'
import {
  Card,
  Row,
  Col,
  Typography,
  Icon,
  Button,
  Separator,
  Skeleton,
} from '@repo/ui/components'
import { useTranslate } from '@repo/shared/hooks'
import { useUserAuthDetails, useUserPortfolio } from '@repo/services'
import { useState } from 'react'
import { ButtonLink, PaymentType } from '@repo/shared/components'
import { cn } from '@repo/ui/lib'
import Image from 'next/image'
import { useLanguage } from '@repo/shared/hooks'
import { ValueWithUnit } from '@repo/shared/components'

interface DashboardEstimatedAssetsProps {
  isDesktop?: boolean
}

export const DashboardEstimatedAssets = (
  props: DashboardEstimatedAssetsProps,
) => {
  const { isDesktop = false } = props
  const { t } = useTranslate()
  const locale = useLanguage()

  const { data, isLoading, isFetching } = useUserPortfolio()
  const estimated = data?.data?.estimated
  const [showBalance, setShowBalance] = useState(true)
  const { data: { data: authDetails } = {} } = useUserAuthDetails()

  const estimatedBalance = estimated?.balance ?? 0
  const delta24H = estimated?.delta24H?.amount ?? 0
  const isNegative = delta24H < 0
  const isLoadingData = isLoading || isFetching

  return (
    <Card
      className={cn(
        'border-none rounded-none md:rounded-md flex flex-col md:flex-row items-start justify-between w-full gap-md md:gap-0',
        !isDesktop && 'dark bg-neutral-surface-dark',
      )}
    >
      {!isDesktop && (
        <Row className='items-center justify-between gap-md mb-xl'>
          <Row className='relative w-[5.25rem] aspect-[3/1]'>
            <Image
              src={`/full-logo-dark-${locale}.svg`}
              alt='logo'
              fill
              className='object-contain'
              sizes='(max-width: 768px) 100vw, 5.25rem'
              priority
            />
          </Row>
          {isLoadingData ? (
            <Skeleton className='w-32 h-6 sm:h-4' />
          ) : (
            <ButtonLink
              variant='transparent'
              color='base'
              size='xs'
              href='/profile'
              startIcon={<Icon name='PersonCircleLine' size='xs' />}
            >
              {authDetails?.details?.firstName}{' '}
              {authDetails?.details?.lastName || t(`profileLayout_my_account`)}
            </ButtonLink>
          )}
        </Row>
      )}
      <Col className='gap-xs md:gap-sm'>
        <Typography
          size='xs'
          color='text-neutral-surface-80'
          className='flex justify-between items-center'
        >
          {t('dashboard_assets_estimated')}
          <ButtonLink
            className='flex md:hidden items-center flex-nowrap '
            variant='transparent'
            color='base'
            size='xs'
            href='/wallet'
            endIcon={
              <Icon
                name='ArrowLeft'
                size='xs'
                color='fill-base-main'
                className='md:size-icon-sm'
              />
            }
          >
            {t('dashboard_assets_title')}
          </ButtonLink>
        </Typography>
        <Row className='items-start md:items-center gap-sm md:gap-md flex-col md:flex-row'>
          <Row className='gap-2xs gap-x-xs md:gap-xs w-fit'>
            {isLoadingData ? (
              <Skeleton className='w-26 h-7 sm:h-7' />
            ) : (
              <ValueWithUnit
                value={estimatedBalance}
                unit='TMN'
                scale={10}
                decimalPlaces={0}
                unitSize='md'
                valueSize='xl'
              />
            )}
            <Button
              variant='ghost'
              color='base'
              size='xs'
              onClick={() => setShowBalance((prev) => !prev)}
              className='ml-xs md:ml-0'
            >
              <Icon
                name={showBalance ? 'EyeClosedLine' : 'EyeOpenedLine'}
                size='xs'
                color='fill-base-main'
                className='md:size-icon-sm'
              />
            </Button>
          </Row>
          {delta24H !== 0 ? (
            <Row className='gap-2xs'>
              {isLoadingData ? (
                <Skeleton className='w-32 h-4 sm:h-4' />
              ) : (
                <>
                  <PaymentType
                    value={delta24H}
                    size='2xs'
                    numberSize='xs'
                    numberColor={
                      isNegative
                        ? 'text-negative-surface-100'
                        : 'text-positive-surface-100'
                    }
                    color={
                      isNegative
                        ? 'text-negative-surface-100'
                        : 'text-positive-surface-100'
                    }
                  />
                  <Typography
                    size='2xs'
                    color={
                      isNegative
                        ? 'text-negative-surface-100'
                        : 'text-positive-surface-100'
                    }
                  >
                    <span>{t('common_change_24h')}</span>
                  </Typography>
                </>
              )}
            </Row>
          ) : (
            <div className='h-md' />
          )}
        </Row>
      </Col>
      <Row className='items-end sm:justify-end gap-xs'>
        <ButtonLink
          className='flex-1 sm:flex-none'
          buttonClassName='!px-md w-full sm:w-fit'
          variant='fill'
          color={isDesktop ? 'base' : 'neutral'}
          size={isDesktop ? 'xs' : 'sm'}
          href='/wallet/deposit'
          startIcon={
            <Icon
              name='Plus'
              size={isDesktop ? 'xs' : 'sm'}
              color='fill-base-main'
              className='md:size-icon-sm'
            />
          }
        >
          {t('common_deposit')}
        </ButtonLink>

        <ButtonLink
          className='flex-1 sm:flex-none'
          buttonClassName='!px-md w-full sm:w-fit'
          variant={isDesktop ? 'transparent' : 'ghost'}
          color={isDesktop ? 'base' : 'neutral'}
          size={isDesktop ? 'xs' : 'sm'}
          href='/wallet/withdraw/online-payment'
          startIcon={
            <Icon
              name='ArrowUp'
              size={isDesktop ? 'xs' : 'sm'}
              color='fill-base-main'
              className='md:size-icon-sm'
            />
          }
        >
          {t('common_withdraw')}
        </ButtonLink>

        <ButtonLink
          className='flex-1 sm:flex-none'
          buttonClassName='!px-md w-full sm:w-fit'
          variant={isDesktop ? 'transparent' : 'ghost'}
          color={isDesktop ? 'base' : 'neutral'}
          size={isDesktop ? 'xs' : 'sm'}
          href='/wallet/transfer'
          startIcon={
            <Icon
              name='ArrowDownLeftArrowUpRight'
              size={isDesktop ? 'xs' : 'sm'}
              color='fill-base-main'
              className='md:size-icon-sm'
            />
          }
        >
          {t('common_transfer')}
        </ButtonLink>
        <Separator
          orientation='vertical'
          className='!h-md mb-2xs !bg-neutral-surface-20 hidden md:block'
        />
        <ButtonLink
          className='hidden md:flex'
          buttonClassName='!px-md'
          variant='transparent'
          color='base'
          size='xs'
          href='/wallet'
          endIcon={
            <Icon
              name='ArrowLeft'
              size='xs'
              color='fill-base-main'
              className='md:size-icon-sm'
            />
          }
        >
          {t('dashboard_assets_title')}
        </ButtonLink>
      </Row>
    </Card>
  )
}
